/**
 * Carrier CLI (stdio) — aggregated tool catalog + scopes.
 *
 * TOOL_REGISTRY (keys of ALL_TOOL_SCOPES) is the set of names carrier_ask may
 * resolve to and carrier_ask_describe may document. Every tool registered via
 * server.registerTool / registerAppTool must appear here.
 *
 * Prefer owning *_TOOL_SCOPES (or name+scope tool defs) in the registration
 * module; this file only aggregates + fills remaining orphans.
 *
 * WHY THIS EXISTS SEPARATELY FROM apps/mcp-server
 * -----------------------------------------------
 * apps/mcp-stdio is a self-contained near-duplicate of apps/mcp-server: its own
 * copy of the tool layer, bundled by tsup into the published @carrierllc/mcp
 * package. It never imports from the Worker app, so nothing done there reaches
 * the installed CLI.
 *
 * That is not theoretical. #723 gave all 121 Worker tools complete MCP safety
 * annotations; the CLI shipped 123 tools with 122 of them missing those hints,
 * because it is a different codebase. This registry is the copy that makes the
 * same central backfill possible here — see src/tool-annotations.ts.
 *
 * The two catalogs are NOT identical and are not meant to be: this package also
 * ships the storefront deploy tools, which the Worker does not.
 */

import type { ToolScope } from "./types.js";
import { TOOL_SCOPES } from "./tools.js";
import { BACKLOG_TOOL_SCOPES } from "./tools-backlog.js";
import { UI_AGENT_TOOL_SCOPES } from "./tools-ui-agent.js";
import { UI_AGENT_ASK_TOOL_SCOPES } from "./tools-ui-agent-ask.js";
import { UI_AGENT_SCHEDULE_TOOL_SCOPES } from "./tools-ui-agent-schedule.js";
import { WALLET_TOOL_SCOPES } from "./wallet-tools.js";
import { STRIPE_CONNECT_TOOL_SCOPES } from "./stripe-connect-tools.js";
import { STOREFRONT_LOGO_TOOL_SCOPES } from "./tools-storefront-logo.js";
import { STOREFRONT_DEPLOY_TOOL_SCOPES } from "./tools-storefront-deploy.js";
import { PRICING_TOOLS } from "./pricing-tools.js";
import { PROJECTS_TOOLS } from "./projects-tools.js";

// ---------------------------------------------------------------------------
// Orphan scope maps (registration modules without exported *_TOOL_SCOPES yet)
// ---------------------------------------------------------------------------

/** Intelligence composites (detect_country_entry lives in TOOL_SCOPES). */
export const INTELLIGENCE_TOOL_SCOPES: Record<string, ToolScope> = {
  diagnose_subscriber: "read",
  fleet_health: "read",
  detect_usage_anomalies: "read",
  optimize_package: "read",
  churn_risk: "read",
  audit_network_coverage: "read",
  marketing_intelligence: "read",
  high_cost_subscribers: "read",
};

/**
 * Generic Steel browsing agent dispatch.
 *
 * Unlike the Worker copy, tools-ui-agent-generic.ts here exports no scope map,
 * so the two names are declared as orphans rather than adding an export to a
 * file this package keeps in sync with the Worker by hand.
 */
export const UI_AGENT_GENERIC_TOOL_SCOPES: Record<string, ToolScope> = {
  ui_agent_ask: "write",
  ui_agent_status: "read",
};

/** Greenzone whitelist tools — handlers enforce these scopes. */
export const GREENZONE_TOOL_SCOPES: Record<string, ToolScope> = {
  greenzone_whitelist_add: "write",
  greenzone_whitelist_remove: "write",
  greenzone_whitelist_list: "read",
};

/** Ops / event buffer tools. */
export const OPS_TOOL_SCOPES: Record<string, ToolScope> = {
  list_recent_ocs_events: "read",
  rate_limit_status: "read",
  subscriber_country_history: "read",
  subscriber_depletion_events: "read",
};

/**
 * MCP Apps + form tools. Documented at the strictest handler-enforced scope
 * (matches generate-docs.mjs HANDLER_ENFORCED_TOOL_SCOPES).
 */
export const APP_TOOL_SCOPES: Record<string, ToolScope> = {
  fleet_health_app: "read",
  provision_esim_wizard: "write",
  balance_topup_form: "admin",
};

/** Self-referential NL router tools. */
export const CARRIER_ASK_TOOL_SCOPES: Record<string, ToolScope> = {
  carrier_ask: "read",
  carrier_ask_describe: "read",
};

/**
 * Tier-gated Stripe Agent Toolkit multiplexer (Pro/Enterprise only).
 * Opaque proxy can perform writes — treat as write for describe/docs.
 */
export const STRIPE_PROXY_TOOL_SCOPES: Record<string, ToolScope> = {
  stripe_proxy_call: "write",
};

function scopesFromToolDefs(
  tools: ReadonlyArray<{ name: string; scope: ToolScope }>,
): Record<string, ToolScope> {
  return Object.fromEntries(tools.map((t) => [t.name, t.scope]));
}

export const PRICING_TOOL_SCOPES: Record<string, ToolScope> = scopesFromToolDefs(PRICING_TOOLS);
export const PROJECTS_TOOL_SCOPES: Record<string, ToolScope> = scopesFromToolDefs(PROJECTS_TOOLS);

// ---------------------------------------------------------------------------
// Aggregate
// ---------------------------------------------------------------------------

/**
 * Complete scope map for every MCP tool name known at build time.
 * Later spreads win on key collision (should not happen).
 */
export const ALL_TOOL_SCOPES: Readonly<Record<string, ToolScope>> = Object.freeze({
  ...TOOL_SCOPES,
  ...BACKLOG_TOOL_SCOPES,
  ...INTELLIGENCE_TOOL_SCOPES,
  ...OPS_TOOL_SCOPES,
  ...PRICING_TOOL_SCOPES,
  ...PROJECTS_TOOL_SCOPES,
  ...APP_TOOL_SCOPES,
  ...UI_AGENT_TOOL_SCOPES,
  ...UI_AGENT_ASK_TOOL_SCOPES,
  ...UI_AGENT_SCHEDULE_TOOL_SCOPES,
  ...UI_AGENT_GENERIC_TOOL_SCOPES,
  ...WALLET_TOOL_SCOPES,
  ...STRIPE_CONNECT_TOOL_SCOPES,
  ...GREENZONE_TOOL_SCOPES,
  ...CARRIER_ASK_TOOL_SCOPES,
  ...STRIPE_PROXY_TOOL_SCOPES,
  ...STOREFRONT_LOGO_TOOL_SCOPES,
  // Storefront deploy tools exist only in this package, not in the Worker.
  ...STOREFRONT_DEPLOY_TOOL_SCOPES,
});

/** Names carrier_ask may resolve to / carrier_ask_describe may document. */
export const TOOL_REGISTRY: ReadonlySet<string> = new Set(Object.keys(ALL_TOOL_SCOPES));

/**
 * Tools present in ALL_TOOL_SCOPES but never registered on the server.
 *
 * `stripe_proxy_call` is a tier-gated Stripe multiplexer that carries a scope
 * entry for describe/docs purposes only. It is not in a `tools/list` response.
 */
export const UNREGISTERED_TOOLS: ReadonlySet<string> = new Set(["stripe_proxy_call"]);

/**
 * The number of tools this server actually exposes — the one every user-facing
 * surface must quote.
 *
 * Derived here so no copy has to hardcode it. This equals what
 * scripts/generate-docs.mjs writes to capability-counts.json from a real
 * `tools/list` response, and is exactly UNREGISTERED_TOOLS fewer than
 * ALL_TOOL_SCOPES has keys. Every public surface previously typed this by hand
 * and they rotted together: the landing page said 113 for months after the true
 * count moved.
 *
 * This comment used to name the two figures (120 and 121) and by 2026-08 both
 * were wrong — the same rot, one level up, in the file that exists to prevent
 * it. The relationship is the durable fact; for today's number read
 * capability-counts.json or docs/tools.md, which are generated.
 *
 * The guard at tests/tool-count-literals.test.ts fails if a literal returns.
 */
export const REGISTERED_TOOL_COUNT: number = Object.keys(ALL_TOOL_SCOPES).filter(
  (name) => !UNREGISTERED_TOOLS.has(name),
).length;

export function getToolScope(toolName: string): ToolScope {
  return ALL_TOOL_SCOPES[toolName] ?? "read";
}
